import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const QUERY = `
  query BundleComponents($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        id
        components(first: 50) {
          nodes {
            quantity
            productVariant {
              id
              title
              image { url altText }
              price { amount currencyCode }
              product {
                title
                handle
                featuredImage { url altText }
              }
            }
          }
        }
      }
    }
  }
`;

class FbBundleContents extends Component {
  requiredRefs = ['inner', 'list', 'loading'];

  connectedCallback() {
    super.connectedCallback();
    this.#repositionAboveDescription();
    this.#load(this.dataset.variantId);

    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
  }

  #repositionAboveDescription() {
    const sig = this.dataset.descSignature;
    if (!sig) return;
    const container = this.closest('.product-details');
    if (!container) return;
    const blocks = container.querySelectorAll('.text-block');
    for (const block of blocks) {
      const text = (block.textContent || '').replace(/\s+/g, ' ').trim();
      if (text.startsWith(sig)) {
        block.parentNode.insertBefore(this, block);
        return;
      }
    }
  }

  // ProductSelectEvent (v4.0.0 @shopify/events) — the resolved variant id can
  // arrive as a numeric "12345" or a GID "gid://shopify/ProductVariant/12345".
  // #load needs the numeric portion to build the Storefront GID itself.
  #onProductSelect = (event) => {
    event.promise
      ?.then(({ variant }) => {
        const rawId = variant?.id;
        if (!rawId) return;
        const numericId = String(rawId).split('/').pop();
        if (!numericId) return;
        this.#load(numericId);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[fb-bundle-contents] product select promise rejected:', error);
        }
      });
  };

  async #load(variantId) {
    if (!variantId) return this.#removeSelf();
    const { shop, apiVersion, token } = this.dataset;
    if (!shop || !token) return this.#removeSelf();

    try {
      const res = await fetch(`https://${shop}/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { id: `gid://shopify/ProductVariant/${variantId}` },
        }),
      });
      const { data, errors } = await res.json();
      if (errors) throw new Error(errors.map((e) => e.message).join(', '));
      if (!data?.node) {
        console.warn('[fb-bundle-contents] variant not found via Storefront API — is the product published to the Headless sales channel?', { variantId });
        return this.#removeSelf();
      }
      const nodes = data.node.components?.nodes ?? [];
      if (!nodes.length) return this.#removeSelf();
      this.#render(nodes);
    } catch (err) {
      console.warn('[fb-bundle-contents] fetch failed', err);
      this.#removeSelf();
    }
  }

  #render(components) {
    const { list, inner, loading } = this.refs;
    list.innerHTML = components.map((c) => {
      const p = c.productVariant.product;
      const img = p.featuredImage?.url ?? c.productVariant.image?.url ?? '';
      const alt = p.featuredImage?.altText ?? p.title;
      const qty = ` <span class="fb-bundle-contents__qty">× ${c.quantity}</span>`;
      const imgHtml = img
        ? `<img class="fb-bundle-contents__img" src="${img}&width=120" alt="${alt}" width="60" height="60" loading="lazy">`
        : '';
      return `
        <li class="fb-bundle-contents__item">
          ${imgHtml}
          <span class="fb-bundle-contents__text">
            <a class="fb-bundle-contents__link" href="/products/${p.handle}">${p.title}</a>${qty}
          </span>
        </li>
      `;
    }).join('');
    loading.hidden = true;
    inner.hidden = false;
  }

  #removeSelf() {
    this.remove();
  }
}

if (!customElements.get('fb-bundle-contents')) {
  customElements.define('fb-bundle-contents', FbBundleContents);
}
